import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/authContext';

const Chat = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeChat, setActiveChat] = useState(null);

  const messagesEndRef = useRef(null);

  const chatRooms = useMemo(() => [
    {
      id: 'team-general',
      name: 'Team General',
      participants: 12,
      lastMessage: 'Great work on the project!',
      unreadCount: 3
    },
    {
      id: 'supervisors-only',
      name: 'Supervisors Only',
      participants: 4,
      lastMessage: 'Meeting scheduled for tomorrow',
      unreadCount: 1
    }
  ], []);

  const sampleMessages = useMemo(() => [
    {
      id: 1,
      sender: 'John Doe',
      senderId: 'user1',
      message: 'Hello team! How is everyone doing today?',
      timestamp: new Date(Date.now() - 300000)
    },
    {
      id: 2,
      sender: user?.firstName + ' ' + user?.lastName,
      senderId: user?.id,
      message: 'Everything is going well. Working on the new features.',
      timestamp: new Date(Date.now() - 240000)
    }
  ], [user?.firstName, user?.lastName, user?.id]);

  useEffect(() => {
    if (chatRooms.length > 0 && !activeChat) {
      setActiveChat(chatRooms[0]);
      setMessages(sampleMessages);
    }
  }, [activeChat, chatRooms, sampleMessages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const message = {
      id: Date.now(),
      sender: user?.firstName + ' ' + user?.lastName,
      senderId: user?.id,
      message: newMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Supervisor Chat</h1>
          <p className="mt-2 text-gray-600">
            Exclusive communication hub for supervisors
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ height: '70vh' }}>
          <div className="flex h-full">
            
            <div className="w-1/3 border-r border-gray-200">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">Chat Rooms</h3>
              </div>
              <div className="overflow-y-auto">
                {chatRooms.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => setActiveChat(chat)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                      activeChat?.id === chat.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <h4 className="font-medium">{chat.name}</h4>
                    <p className="text-sm text-gray-500">{chat.participants} participants</p>
                    {chat.unreadCount > 0 && (
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                        {chat.unreadCount}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 flex flex-col">
              {activeChat ? (
                <>
                  <div className="p-4 border-b">
                    <h2 className="font-semibold">{activeChat.name}</h2>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map((message) => {
                      const isOwnMessage = message.senderId === user?.id;
                      return (
                        <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs px-3 py-2 rounded-lg ${
                            isOwnMessage 
                              ? 'bg-blue-500 text-white' 
                              : 'bg-gray-100 text-gray-900'
                          }`}>
                            {!isOwnMessage && (
                              <p className="text-xs font-semibold mb-1">{message.sender}</p>
                            )}
                            <p className="text-sm">{message.message}</p>
                            <p className="text-xs mt-1 opacity-75">
                              {formatTime(message.timestamp)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="p-4 border-t">
                    <form onSubmit={handleSendMessage} className="flex space-x-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                      >
                        Send
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-gray-500">Select a chat room to start messaging</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            🔒 This chat is exclusively for supervisors only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Chat; 